import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../firebaseConfig';
import { syncClaims, hasClaims } from '../utils/syncClaims';

const CompanyContext = createContext();

export const useCompany = () => {
  return useContext(CompanyContext);
};

// Панель водителя одна на всех партнёров: кто именно открыл страницу,
// определяется по домену. Раньше для этого искали компанию прямо в коллекции
// companies, но она закрыта — там Stripe customerId и статус подписки, а запрос
// идёт ещё до входа водителя. Поэтому читаем публичную витрину sites/{домен},
// где лежит только название фирмы, адрес, телефон и её идентификатор.
//
// Если свой домен ещё не подключён, компанию передаёт ссылка-приглашение
// вида ...?firma=NIP — она же запоминается, чтобы второй раз водитель мог
// зайти просто по адресу панели.
export const CompanyProvider = ({ children }) => {
  const [companyData, setCompanyData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [claimsReady, setClaimsReady] = useState(false);

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const fromLink = params.get('firma');
        if (fromLink) localStorage.setItem('fleetify:firma', fromLink);

        const remembered = localStorage.getItem('fleetify:firma');
        // Домен важнее: если партнёр подключил свой, он и определяет компанию.
        const candidates = [window.location.hostname, fromLink, remembered].filter(Boolean);

        let data = null;
        for (const key of candidates) {
          const snap = await getDoc(doc(db, 'sites', key));
          if (snap.exists()) { data = snap.data(); break; }
        }

        // Ссылку выдал партнёр, а витрины под его идентификатором нет —
        // этого достаточно, чтобы панель работала: остальное подтянется после входа.
        if (!data && (fromLink || remembered)) {
          data = { companyId: fromLink || remembered };
        }

        if (data && data.companyId) {
          setCompanyData({
            companyId: data.companyId,
            companyName: data.companyName || '',
            companyAdress: data.address || '',
            phone: data.phone || '',
          });
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error('Nie udało się ustalić firmy:', error);
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanyData();
  }, []);

  // Права выдаёт сервер, и до их появления панель пуста: правила откажут
  // и в фактурах, и в расчётах. Поэтому при первом входе ждём ответ, а если
  // права уже в токене — рисуем сразу и обновляем их фоном.
  useEffect(() => {
    if (!companyData || !companyData.companyId) return undefined;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) { setClaimsReady(true); return; }

      if (await hasClaims(user)) {
        setClaimsReady(true);
        syncClaims(user, companyData.companyId);
      } else {
        await syncClaims(user, companyData.companyId);
        setClaimsReady(true);
      }
    });

    return unsubscribe;
  }, [companyData]);

  if (isLoading) return null;
  if (companyData && !claimsReady) return null;

  if (notFound) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 12,
        padding: 24, textAlign: 'center', fontFamily: 'Manrope, sans-serif'
      }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Nie rozpoznajemy tej firmy</h1>
        <p style={{ fontSize: 14, maxWidth: 380, lineHeight: 1.6, opacity: .7, margin: 0 }}>
          Otwórz panel linkiem, który dostałeś od swojego partnera —
          powinien zawierać adres jego firmy.
        </p>
      </div>
    );
  }

  return (
    <CompanyContext.Provider value={companyData}>
      {children}
    </CompanyContext.Provider>
  );
};
